import React, {Fragment, memo} from 'react';
import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import {colors, fontFamily, fontWeight, fontSize, lineHeight} from '@/styles/tokens';

const DisplayImage = dynamic(import('@/components/Ui-components/display-image'));

const headerWrapperSx = {
    textAlign: 'center'
} as const;

const headingSx = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[32],
    fontWeight: fontWeight.bold,
    maxWidth: '900px',
    mx: 'auto',
    '& span': {
        fontFamily: fontFamily.heading,
        fontSize: fontSize[32],
        color: colors.primary.main,
        fontWeight: fontWeight.bold
    },
    '& a': {
        textDecoration: 'none',
        color: colors.text.primary
    }
} as const;

const dividerWrapperSx = {
    pb: '20px'
} as const;

const dividerSx = {
    maxWidth: '130px',
    height: '5px',
    backgroundImage: 'linear-gradient(to left, rgba(255, 91, 32, 0), rgba(255, 91, 32, 1))',
    border: 'none',
    display: 'block',
    mx: 'auto',
    my: 0
} as const;

const subheadingWrapperSx = {
    px: {xs: 0, sm: '20px', md: '80px', lg: '100px'}
} as const;

const subheadingSx = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[16],
    pb: '15px',
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[28],
    color: colors.text.primary,
    '& a': {textDecoration: 'none'}
} as const;

const categoryHeadingSx = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    pb: '15px'
} as const;

const techListSx = {
    pl: 0,
    gap: '10px',
    py: 0
} as const;

const techListItemSx = {
    px: 0,
    gap: '10px',
    alignItems: 'center'
} as const;

const iconBoxSx = {
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden'
} as const;

const listItemTextSx = {
    '& span': {
        fontSize: fontSize[16],
        fontWeight: fontWeight.regular,
        fontFamily: fontFamily.body
    }
} as const;

const nestedSectionSx = {
    pt: '10px'
} as const;

const renderedList = (technologyData: any) => {
    return technologyData?.map((parent: any, index: number) => {
        return (
            <Fragment key={`parent_${index + 1}`}>
                <MUIGrid item xs={6} sm={6} md={2.4} lg={2.4} xl={2.4}>
                    <MUIBox>
                        <MUITypography variant="h6" sx={categoryHeadingSx}>
                            {parent.heading}
                        </MUITypography>
                        {parent &&
                            parent.values?.map((child: any, childIndex: number) => {
                                const hasNested = child.nestedData && child.nestedData.length >= 1;
                                return (
                                    <MUIBox key={`item-${childIndex + 1}-tech`} sx={hasNested ? nestedSectionSx : undefined}>
                                        {child.label && child.icon && (
                                            <List subheader={<li />} sx={techListSx}>
                                                <ListItem sx={techListItemSx}>
                                                    <MUIBox sx={iconBoxSx}>
                                                        <DisplayImage
                                                            src={child.icon}
                                                            alt={child.label}
                                                            width={30}
                                                            height={30}
                                                            priority={false}
                                                        />
                                                    </MUIBox>
                                                    <ListItemText sx={listItemTextSx} primary={child.label} />
                                                </ListItem>
                                            </List>
                                        )}

                                        <MUIBox className={child.className || undefined}>
                                            {hasNested && (
                                                <MUITypography variant="h6" sx={categoryHeadingSx}>
                                                    {child.heading}
                                                </MUITypography>
                                            )}
                                            {hasNested &&
                                                child.nestedData?.map((item: any, nestedIndex: number) => {
                                                    return (
                                                        <List key={nestedIndex} subheader={<li />} sx={techListSx}>
                                                            <ListItem sx={techListItemSx}>
                                                                <MUIBox sx={iconBoxSx}>
                                                                    <DisplayImage
                                                                        src={item.icon}
                                                                        alt={item.label}
                                                                        width={30}
                                                                        height={30}
                                                                        priority={false}
                                                                    />
                                                                </MUIBox>
                                                                <ListItemText sx={listItemTextSx} primary={item.label} />
                                                            </ListItem>
                                                        </List>
                                                    );
                                                })}
                                        </MUIBox>
                                    </MUIBox>
                                );
                            })}
                    </MUIBox>
                </MUIGrid>
            </Fragment>
        );
    });
};

const Technology = (props: any) => {
    const {technologyData, heading, subheading = null, divider} = props;

    return (
        <React.Fragment>
            <MUIBox sx={headerWrapperSx}>
                <MUITypography sx={headingSx}>{parse(heading)}</MUITypography>
                {divider === 'none' ? null : (
                    <MUIBox sx={dividerWrapperSx}>
                        <MUIBox component="hr" sx={dividerSx} />
                    </MUIBox>
                )}
                <MUIBox sx={subheadingWrapperSx}>
                    <MUITypography sx={subheadingSx}>{subheading ? parse(subheading) : ''}</MUITypography>
                </MUIBox>
            </MUIBox>
            <MUIGrid container spacing={2} pt={subheading !== null ? 2 : 0}>
                {renderedList(technologyData)}
            </MUIGrid>
        </React.Fragment>
    );
};

export default memo(Technology);
